import { useEffect, useRef } from 'react'
import JSMpeg from '@cycjimmy/jsmpeg-player'

type Options = {
  /** Первый успешно декодированный видеокадр (не путать с «stream ready»). */
  onFirstFrame?: () => void
}

function paintBlack(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, canvas.width || 1280, canvas.height || 720)
}

export function useJsmpegPlayer(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  streamUrl: string | null,
  opts?: Options,
) {
  const playerRef = useRef<InstanceType<typeof JSMpeg.Player> | null>(null)
  const onFirstFrameRef = useRef(opts?.onFirstFrame)
  onFirstFrameRef.current = opts?.onFirstFrame

  useEffect(() => {
    const canvas = canvasRef.current
    if (!streamUrl || !canvas) return

    try {
      playerRef.current?.destroy()
    } catch {
      /* ignore */
    }
    playerRef.current = null

    paintBlack(canvas)

    let gotFrame = false
    const markFrame = () => {
      if (gotFrame) return
      gotFrame = true
      onFirstFrameRef.current?.()
    }

    playerRef.current = new JSMpeg.Player(streamUrl, {
      canvas,
      audio: false,
      autoplay: true,
      streaming: true,
      disableGl: true,
      onVideoDecode: markFrame,
      onPlay: markFrame,
    })

    // Fallback: если колбэки плеера не сработали, ждём непустые пиксели
    const poll = window.setInterval(() => {
      if (gotFrame) {
        window.clearInterval(poll)
        return
      }
      try {
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx || !canvas.width || !canvas.height) return
        const sample = ctx.getImageData(
          Math.floor(canvas.width / 2),
          Math.floor(canvas.height / 2),
          1,
          1,
        ).data
        // не чисто чёрный и не «пустой» — считаем кадром
        if (sample[0] > 8 || sample[1] > 8 || sample[2] > 8) {
          markFrame()
          window.clearInterval(poll)
        }
      } catch {
        /* ignore */
      }
    }, 200)

    return () => {
      window.clearInterval(poll)
      try {
        playerRef.current?.destroy()
      } catch {
        /* ignore */
      }
      playerRef.current = null
      paintBlack(canvas)
    }
  }, [streamUrl, canvasRef])
}

export function clearCanvas(canvas: HTMLCanvasElement | null) {
  if (!canvas) return
  paintBlack(canvas)
}
